import { useCallback, useState } from 'react';
import { useDiagramStore } from '../stores/diagramStore';
import { snapshotFromStore } from '../stores/undoStore';
import type { DiagramPage } from '../types';

export interface UsePageNavigationOptions {
  pages: DiagramPage[];
  activePage: string;
  switchPage: (pageId: string) => void;
  /** Underlying store action to delete a page (`useDiagramStore.getState().deletePage`). */
  deletePage: (pageId: string) => void;
  /** Whether the current interaction mode allows mutating page structure. */
  canMutateStructure: boolean;
  /** Surface a transient toast message — typically wired to the App's toast hook. */
  showToast: (message: string) => void;
}

export interface UsePageNavigationResult {
  /** Stable ordered list of recently visited page IDs. Front=oldest, back=most recent. */
  pageHistory: string[];
  /** Current pointer into `pageHistory` (-1 if empty). */
  historyIndex: number;
  /** Switch to `pageId`, deduplicating its prior occurrence and truncating forward history. */
  switchPageWithHistory: (pageId: string) => void;
  /** Step back one entry in `pageHistory`, if possible. */
  goBack: () => void;
  /** Step forward one entry in `pageHistory`, if possible. */
  goForward: () => void;
  /** Jump to the first page (with history). */
  goHome: () => void;
  /** Delete a page, refusing to delete the last page; updates history accordingly. */
  handleDeletePage: (pageId: string) => void;
}

/**
 * Manages multi-page navigation history (back/forward stack with deduplication).
 *
 * The hook owns `pageHistory` + `historyIndex` and exposes pure callbacks that wrap
 * the store's `switchPage` / `deletePage` actions. Toast surfaces are delegated to the
 * caller via `showToast`.
 */
export function usePageNavigation({
  pages,
  activePage,
  switchPage,
  deletePage,
  canMutateStructure,
  showToast,
}: UsePageNavigationOptions): UsePageNavigationResult {
  const [pageHistory, setPageHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const switchPageWithHistory = useCallback((pageId: string) => {
    if (pageId === activePage) return;
    // Dedupe: remove any prior occurrence, then truncate forward history and append.
    const filtered = pageHistory.filter((id) => id !== pageId);
    const newHistory = filtered.slice(0, historyIndex + 1);
    newHistory.push(pageId);
    setPageHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    switchPage(pageId);
  }, [activePage, pageHistory, historyIndex, switchPage]);

  const goBack = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      switchPage(pageHistory[newIndex]);
    }
  }, [historyIndex, pageHistory, switchPage]);

  const goForward = useCallback(() => {
    if (historyIndex < pageHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      switchPage(pageHistory[newIndex]);
    }
  }, [historyIndex, pageHistory, switchPage]);

  const goHome = useCallback(() => {
    if (pages.length > 0) {
      switchPageWithHistory(pages[0].id);
    }
  }, [pages, switchPageWithHistory]);

  const handleDeletePage = useCallback((pageId: string) => {
    if (!canMutateStructure) return;
    if (pages.length <= 1) {
      showToast('Cannot delete the last page');
      return;
    }
    const page = pages.find((entry) => entry.id === pageId);
    if (!page) return;

    snapshotFromStore();
    deletePage(pageId);

    const nextActivePage = useDiagramStore.getState().activePage;
    const filteredHistory = pageHistory.filter((id) => id !== pageId);
    const nextHistory = filteredHistory.length > 0
      ? filteredHistory
      : (nextActivePage ? [nextActivePage] : []);

    setPageHistory(nextHistory);
    setHistoryIndex(nextHistory.length > 0 ? Math.min(historyIndex, nextHistory.length - 1) : -1);
    showToast(`Deleted page "${page.name}"`);
  }, [pages, deletePage, pageHistory, historyIndex, canMutateStructure, showToast]);

  return {
    pageHistory,
    historyIndex,
    switchPageWithHistory,
    goBack,
    goForward,
    goHome,
    handleDeletePage,
  };
}
